<script setup lang="ts">
  import type { Column } from '@tanstack/vue-table';

  import { Button } from '@ui/components/button';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';
  import { cn } from '@ui/utils/utils';

  interface DataTableColumnHeaderProps {
    column: Column<any, any>;
    title?: string;
    tooltipText?: string;
  }

  const props = defineProps<DataTableColumnHeaderProps>();

  function toggleSort() {
    if (!props.column.getIsSorted()) {
      props.column.toggleSorting(true);
    } else if (props.column.getIsSorted() === 'desc') {
      props.column.toggleSorting(false);
    } else {
      props.column.clearSorting();
    }
  }
</script>

<template>
  <div v-if="column.getCanSort()" :class="cn('flex items-center space-x-2', $attrs.class ?? '')">
    <Button
      variant="ghost"
      size="inline"
      class="data-[state=open]:bg-background-muted px-0 hover:bg-transparent h-fit! space-x-0.5 [&>i]:text-2xs!"
      @click="toggleSort()"
    >
      <div class="flex gap-x-2 items-center">
        <span>{{ title }}</span>

        <Tooltip v-if="tooltipText">
          <TooltipTrigger as-child>
            <i class="far fa-info-circle text-muted-foreground text-2xs!" />
          </TooltipTrigger>

          <TooltipContent class="text-xs">{{ tooltipText }}</TooltipContent>
        </Tooltip>
      </div>

      <slot />

      <i v-if="column.getIsSorted() === 'desc'" class="far fa-arrow-down" />

      <i v-else-if="column.getIsSorted() === 'asc'" class="far fa-arrow-up" />

      <i v-else class="far fa-arrow-up-arrow-down" />
    </Button>
  </div>

  <div v-else :class="$attrs.class" class="flex items-center gap-1 truncate text-sans">
    {{ title }}

    <Tooltip v-if="tooltipText">
      <TooltipTrigger as-child>
        <i class="far fa-info-circle text-muted-foreground text-2xs!" />
      </TooltipTrigger>
      <TooltipContent class="text-xs">{{ tooltipText }}</TooltipContent>
    </Tooltip>
  </div>
</template>
